import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Tag, Tooltip } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import IloIndicatorMappingModal from "components/modals/IloIndicatorMappingModal";

const { Text } = Typography;
const { Search } = Input;

export default function IloIndicatorMappingTab({ refreshTrigger, onCheckSuccess }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [query, setQuery] = useState("");
  const indicatorsState = useSelector((state) => state.indicators);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    fetchMappings();
  }, [refreshTrigger]);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await AxiosOICStat.get("/external_api/ilo/indicator-mappings");
      setData(res.data.mappings || []);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load ILO indicator mappings. Please try again.";
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data;

    const q = query.toLowerCase();
    return data.filter((r) => {
      const indicator = indicatorsState.value ? indicatorsState.value[r.ind_code] : null;
      const indicatorName = indicator && indicator.ind_name_eng ? indicator.ind_name_eng : '';

      const indCode = String(r.ind_code || "");
      const iloCode = String(r.ilo_code || "").toLowerCase();
      const type = String(r.type || "").toLowerCase();
      const classOne = String(r.class_one || "").toLowerCase();
      const classTwo = String(r.class_two || "").toLowerCase();
      const formulation = String(r.formulation || "").toLowerCase();

      return (
        indCode.includes(q) ||
        iloCode.includes(q) ||
        type.includes(q) ||
        classOne.includes(q) ||
        classTwo.includes(q) ||
        formulation.includes(q) ||
        indicatorName.toLowerCase().includes(q)
      );
    });
  }, [data, query, indicatorsState.value]);

  const handleCheck = (id) => async () => {
    setCheckingId(id);
    try {
      const res = await AxiosOICStat.post(`/external_api/ilo/check-indicator/${id}`);
      const newStatus = res.data?.status;
      setData(prev => prev.map(d => d.id === id ? { ...d, status: newStatus, last_check_time: res.data?.last_check_time } : d));
      message.success(newStatus ? 'API data available' : 'No data from API');
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Check failed');
    } finally { setCheckingId(null); }
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const startRes = await AxiosOICStat.post("/external_api/ilo/check-all-indicators");
      const { task_id, total } = startRes.data;

      // Poll for completion
      let completed = false;
      let results = [];
      while (!completed) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await AxiosOICStat.get(`/external_api/ilo/check-all-indicators/status/${task_id}`);
        const task = statusRes.data;
        if (task.status === "completed") {
          completed = true;
          results = task.results || [];
        } else if (task.status === "error") {
          throw new Error(task.error || "Check all failed");
        }
      }

      setData(prev => prev.map(d => {
        const found = results.find(r => r.id === d.id);
        return found ? { ...d, status: found.status, last_check_time: found.last_check_time } : d;
      }));
      const ok = results.filter(r => r.status === true).length;
      const fail = results.filter(r => r.status === false).length;
      message.success(`Checked all: ${ok} OK, ${fail} no data`);
    } catch (err) {
      message.error(err?.response?.data?.detail || err.message || 'Check all failed');
    } finally { setCheckingAll(false); if (onCheckSuccess) onCheckSuccess(); }
  };

  const handleEdit = (record) => () => {
    setEditingRecord(record);
    setAddModalVisible(true);
  };

  const handleDelete = (id) => () => {
    Modal.confirm({
      title: 'Delete ILO Indicator Mapping',
      content: `Are you sure you want to delete this mapping? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await AxiosOICStat.delete(`/external_api/ilo/indicator-mappings/${id}`);
          setData((prevData) => prevData.filter((item) => item.id !== id));
          message.success(`Successfully deleted mapping`);
        } catch (err) {
          const detail =
            err?.response?.data?.detail ||
            err?.message ||
            `Failed to delete mapping. Please try again.`;
          message.error(detail);
        }
      }
    });
  };

  const columns = [
    {
      title: "Indicator Code",
      dataIndex: "ind_code",
      key: "ind_code",
      width: 100,
      sorter: (a, b) => {
        const aVal = a.ind_code;
        const bVal = b.ind_code;
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        const aIsNum = !Number.isNaN(aNum);
        const bIsNum = !Number.isNaN(bNum);
        if (aIsNum && bIsNum) return aNum - bNum;
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        return String(aVal || '').localeCompare(String(bVal || ''));
      },
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "Indicator Name",
      dataIndex: "ind_code",
      key: "indicator_name",
      width: 200,
      sorter: (a, b) => {
        const aIndicator = indicatorsState.value?.[a.ind_code];
        const bIndicator = indicatorsState.value?.[b.ind_code];
        const aName = aIndicator?.ind_name_eng || '';
        const bName = bIndicator?.ind_name_eng || '';
        return aName.localeCompare(bName);
      },
      render: (indCode) => {
        const indicator = indicatorsState.value?.[indCode];
        return indicator ? (
          <Text>{indicator.ind_name_eng}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "ILO Code",
      dataIndex: "ilo_code",
      key: "ilo_code",
      width: 220,
      sorter: (a, b) => (a.ilo_code || '').localeCompare(b.ilo_code || ''),
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Concanaete Code",
      dataIndex: "concanaete_code",
      key: "concanaete_code",
      width: 440,
      sorter: (a, b) => (a.concanaete_code || '').localeCompare(b.concanaete_code || ''),
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 70,
      sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Sex",
      dataIndex: "sex",
      key: "sex",
      width: 70,
      sorter: (a, b) => (a.sex || '').localeCompare(b.sex || ''),
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Class One",
      dataIndex: "class_one",
      key: "class_one",
      width: 180,
      sorter: (a, b) => (a.class_one || '').localeCompare(b.class_one || ''),
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Class Two",
      dataIndex: "class_two",
      key: "class_two",
      width: 180,
      sorter: (a, b) => (a.class_two || '').localeCompare(b.class_two || ''),
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Formulation / Multiplier",
      dataIndex: "formulation",
      key: "formulation",
      width: 300,
      sorter: (a, b) => (String(a.formulation || '')).localeCompare(String(b.formulation || '')),
      render: (val, record) => {
        const mult = record && record.multiplier ? Number(record.multiplier) : 1;
        if (val) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text>{val}</Text>
              {mult && mult !== 1 ? (
                <Tag color="blue" style={{ alignSelf: 'flex-start', display: 'inline-block' }}>×{mult}</Tag>
              ) : null}
            </div>
          );
        }
        if (mult && mult !== 1) {
          return (
            <Tooltip title={`Multiplier: ×${mult}`}>
              <Tag color="blue" style={{ display: 'inline-block' }}>×{mult}</Tag>
            </Tooltip>
          );
        }
        return <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 240,
      sorter: (a, b) => {
        const sa = a.status === true ? 1 : a.status === false ? -1 : 0;
        const sb = b.status === true ? 1 : b.status === false ? -1 : 0;
        return sb - sa;
      },
      render: (v, record) => {
        const timeStr = record.last_check_time ? new Date(record.last_check_time).toLocaleString() : null;
        if (v === true) return <Space><Tag icon={<CheckCircleOutlined />} color="success">Available</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        if (v === false) return <Space><Tag icon={<CloseCircleOutlined />} color="error">Unavailable</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        return <Tag icon={<QuestionCircleOutlined />} color="default">Unknown</Tag>;
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Check API for data availability">
            <Button
              icon={checkingId === record.id ? <SyncOutlined spin /> : <CheckCircleOutlined />}
              size="small"
              onClick={handleCheck(record.id)}
              loading={checkingId === record.id}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            style={{ width: 32 }}
            onClick={handleEdit(record)}
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            style={{ width: 32 }}
            onClick={handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => {
      const aName = a.ind_name_eng || '';
      const bName = b.ind_name_eng || '';
      return aName.localeCompare(bName);
    })
    : [];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space>
        <Search
          allowClear
          placeholder="Search by indicator code, name, ILO code, type, class…"
          onSearch={setQuery}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalVisible(true)}
        >
          Add New Indicator Mapping
        </Button>
        <Button icon={checkingAll ? <SyncOutlined spin /> : <CheckCircleOutlined />} onClick={handleCheckAll} loading={checkingAll}>Check All</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        bordered
      />

      <IloIndicatorMappingModal
        visible={addModalVisible}
        record={editingRecord}
        onCancel={() => {
          setAddModalVisible(false);
          setEditingRecord(null);
        }}
        onSuccess={() => {
          setAddModalVisible(false);
          setEditingRecord(null);
          fetchMappings();
        }}
      />
    </Space>
  );
}
